import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useState } from 'react';

interface ChapterUnansweredCommentsBadgeProps {
  mainContentId: string | null | undefined;
}

export function ChapterUnansweredCommentsBadge({ mainContentId }: ChapterUnansweredCommentsBadgeProps) {
  const { backend } = useFirebase();
  const { novel } = useNovel();
  const [unresolvedCount, setUnresolvedCount] = useState<number | null>(null);

  useEffect(() => {
    if (!novel.id || !mainContentId) {
      setUnresolvedCount(0);
      return;
    }

    const unsubscribe = backend.comments.subscribeComments(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      mainContentId,
      (comments) => {
        const openCount = comments.filter((c) => c && c.status === 'open').length;
        setUnresolvedCount(openCount);
      },
      (err) => {
        console.error('Failed to listen to comments for badge:', err);
        setUnresolvedCount(0);
      }
    );

    return () => unsubscribe();
  }, [backend, novel, mainContentId]);

  if (unresolvedCount === null || unresolvedCount === 0) {
    return null;
  }

  return (
    <Tooltip title={`${unresolvedCount} unanswered comments`}>
      <Badge
        badgeContent={unresolvedCount}
        color="warning"
        sx={{
          ml: 0.5,
          mr: 1,
          '& .MuiBadge-badge': {
            fontSize: '0.6rem',
            fontWeight: 900,
            height: 16,
            minWidth: 16,
            borderRadius: '8px',
          },
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 15, color: 'warning.main' }} />
      </Badge>
    </Tooltip>
  );
}
